import { useBookingCommerceStore } from '@/stores/bookingCommerce'
import { useCartStore } from '@/stores/cart'
import { useMainStore } from '@/stores/main'
import type { SessionTypeKey } from '@/stores/sessionType'
import { storeId } from '@/stores/storeId'
import { defineStore } from 'pinia'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

import { CadyAddOn } from '@/lib/cady/cadyEnums'
import { CADY_SESSION_TYPE_ID_ATTR } from '@/lib/cartItemAttributes'
import { formatLocationFullAddress } from '@/lib/locationAddress'
import {
  buildSchedulingCustomizations,
  getMaxSceneCount,
  getRequiredSceneCount,
  getSessionTierByKey,
  isRoadshowPlusKey,
  keyRequiresPresentation,
  reconcileSceneSelections,
  SessionTier
} from '@/lib/sessionCatalog'
import { getBundleIncludedGroupItemAttribute } from '@/lib/sessionCatalog'
import Sunny16CommerceCadyService from '@/services/Sunny16CommerceCadyService'

import type { AddOnSelectionState } from '@/components/AddOnsSelection/types'
import { SchedulingLocationToggleType } from '@/components/Scheduling/types'

import type { BookingSessionTypeDtoWithContent } from '@/types/catalogWithContent'
import type { SessionBundleContent } from '@/types/sessionBundleEnriched'

import {
  CadySessionType,
  type LocationModel,
  type ReserveAppointmentRequest
} from '@VelocitySquared/cady-sunny16-commerce-integration-models'
import type { LandingLocationOption } from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

dayjs.extend(customParseFormat)

/**
 * Sentinel for the "Buy later" selection state on Checkout Prepay. Stored in
 * `selectedPrepayOption` to make the state mutually exclusive with prepay-card
 * selections while keeping `selectedPrepayCatalogBundleId` null (no cart bundle
 * is bound when the user opts to buy later).
 */
export const BUY_LATER_OPTION = 'buy-later' as const

/**
 * Fixed duration (in minutes) requested for every appointment-reservation hold,
 * independent of the picked slot's actual duration (`scheduledSessionDuration` /
 * `scheduledPresentationDuration`), which continues to be tracked separately for
 * cart sync and display.
 */
export const RESERVATION_HOLD_DURATION_MINUTES = 30

/** Result of an appointment-reservation attempt (`reserveSessionAppointment` / `reservePresentationAppointment`). */
interface AppointmentReservationOutcome {
  success: boolean
  message?: string
}

/** Combines a `YYYY-MM-DD` date string and a time string (e.g. "2:00 PM") into the naive local timestamp CoreAPI expects for `ReserveAppointmentRequest.AppointmentStart` (e.g. "2026-08-19T10:30:00" — no milliseconds, no `Z` suffix). */
function combineDateAndTime(date: string, time: string): string {
  const parsedTime = dayjs(time, ['h:mm A', 'H:mm:ss', 'H:mm'])
  const combined = dayjs(date)
    .hour(parsedTime.hour())
    .minute(parsedTime.minute())
    .second(0)
    .format('YYYY-MM-DDTHH:mm:ss')
  return combined
}

interface BookingSelectionsStoreState {
  // --- Booking flow selections ---
  /** The location the user chose (Studio or School). Set on the landing page. */
  selectedLocation: LandingLocationOption | null
  /** The selected session type (API DTO + content). */
  selectedSessionType: BookingSessionTypeDtoWithContent | null
  /** Scene IDs the user has selected (checked) for their session. */
  selectedSceneIds: string[]
  /** Scene IDs the user has favorited (hearted). Set on PLP, refined on Scene Selection. */
  favoritedSceneIds: string[]
  /** Add-on selection states, keyed by section ID. */
  addOnSelections: Record<string, AddOnSelectionState>
  /** Identifier of the chosen item within the HAIR_OR_MAKEUP bundle (e.g. 'HAIR' or 'MAKEUP'). */
  hairOrMakeupItemIdentifier: string | null
  /** Studio vs School toggle for scheduling. */
  selectedLocationType: SchedulingLocationToggleType
  /** The selected location (studio) for the session. */
  scheduledLocation: LocationModel | null
  /** Selected session date (ISO string). */
  scheduledSessionDate: string | null
  /** Selected session time string (e.g. "2:00 PM"). */
  scheduledSessionTime: string | null
  /** Surcharge tier for the selected session date (e.g. "Tier 1"), or null. */
  scheduledSessionSurchargeTier: string | null
  /** Duration of the selected session slot in minutes. */
  scheduledSessionDuration: number | null
  /** Server-issued signature for the selected session slot. */
  scheduledSessionSig: string | null
  /** Server-issued hold ID for the session appointment, from `reserveAppointment`. */
  sessionAppointmentReservationId: number | null
  /** Expiration (ISO string) of the session appointment hold. */
  sessionAppointmentExpiration: string | null
  /** Server-assigned device identifier from a successful session `reserveAppointment` call. Reused on subsequent calls; null until the first success. */
  sessionAppointmentDeviceId: string | null
  /** Selected presentation date (ISO string). Elite sessions only. */
  scheduledPresentationDate: string | null
  /** Selected presentation time string. Elite sessions only. */
  scheduledPresentationTime: string | null
  /** Surcharge tier for the selected presentation date, or null. */
  scheduledPresentationSurchargeTier: string | null
  /** Duration of the selected presentation slot in minutes. */
  scheduledPresentationDuration: number | null
  /** Server-issued signature for the selected presentation slot. */
  scheduledPresentationSig: string | null
  /** Server-issued hold ID for the presentation appointment, from `reserveAppointment`. */
  presentationAppointmentReservationId: number | null
  /** Expiration (ISO string) of the presentation appointment hold. */
  presentationAppointmentExpiration: string | null
  /** Server-assigned device identifier from a successful presentation `reserveAppointment` call. Reused on subsequent calls; null until the first success. */
  presentationAppointmentDeviceId: string | null
  /**
   * The presentation session type ID returned by the session availability API.
   * Persisted so the presentation availability call can be made on page restore
   * (navigate back) without needing to re-call the session availability API.
   */
  presentationSessionTypeId: number | null
  // --- Cart sync ---
  /**
   * True when the session type has changed since the last successful cart upsert
   * at Scene Selection. Prevents forward navigation past Scene Selection until
   * the cart is updated. Cleared by Scene Selection on successful upsert.
   */
  sessionBundleDirty: boolean
  // --- Checkout selections (moved from checkoutStore) ---
  /** Catalog bundle IDs of cart upsells the user has explicitly declined. Prevents re-adding on page revisit. */
  declinedCartUpsellIds: string[]
  /** Selected prepay option card id, or null if nothing selected */
  selectedPrepayOption: string | null
  /** API catalog bundle ID of the selected prepay option, for cart operations */
  selectedPrepayCatalogBundleId: string | null
  /** Whether the Premier Retouching drawer is currently visible */
  showRetouchingDrawer: boolean
  /** API catalog bundle ID for Premier Retouching, for cart operations */
  premierRetouchingCatalogBundleId: string | null
  /** Per-checkbox acceptance state for terms page, keyed by checkbox id */
  termsCheckboxes: Record<string, boolean>
  /** Model release consent state: true = consented/opted-out (depending on polarity). `null` means the customer has not answered yet — the proof-type default seeds the box on first visit to the Terms page and never overwrites an explicit answer afterwards. */
  modelReleaseChecked: boolean | null
  /** Portfolio opt-in: true = customer wants the portfolio (default), false = opted out. Drives the "I would like to receive my Proof Portfolio" checkbox. */
  portfolioOptInChecked: boolean
}

export const useBookingSelectionsStore = defineStore(storeId.BOOKING_SELECTIONS, {
  state: (): BookingSelectionsStoreState => ({
    selectedLocation: null,
    selectedSessionType: null,
    selectedSceneIds: [],
    favoritedSceneIds: [],
    addOnSelections: {},
    hairOrMakeupItemIdentifier: null,
    selectedLocationType: SchedulingLocationToggleType.Studio,
    scheduledLocation: null,
    scheduledSessionDate: null,
    scheduledSessionTime: null,
    scheduledSessionSurchargeTier: null,
    scheduledSessionDuration: null,
    scheduledSessionSig: null,
    sessionAppointmentReservationId: null,
    sessionAppointmentExpiration: null,
    sessionAppointmentDeviceId: null,
    scheduledPresentationDate: null,
    scheduledPresentationTime: null,
    scheduledPresentationSurchargeTier: null,
    scheduledPresentationDuration: null,
    scheduledPresentationSig: null,
    presentationAppointmentReservationId: null,
    presentationAppointmentExpiration: null,
    presentationAppointmentDeviceId: null,
    presentationSessionTypeId: null,
    sessionBundleDirty: false,
    declinedCartUpsellIds: [],
    selectedPrepayOption: null,
    selectedPrepayCatalogBundleId: null,
    showRetouchingDrawer: false,
    premierRetouchingCatalogBundleId: null,
    termsCheckboxes: {},
    modelReleaseChecked: null,
    portfolioOptInChecked: true
  }),
  getters: {
    /**
     * Content bundle for the selected session type.
     * Backward-compatible getter — existing consumers that read
     * display fields (title, images, features, etc.) use this.
     */
    selectedBundle(): SessionBundleContent | null {
      return this.selectedSessionType?.primarySessionBundle.content ?? null
    },
    /** Whether a session type has been selected. */
    hasSessionTypeSelected(): boolean {
      return this.selectedSessionType !== null
    },
    /** The SessionTypeKey of the selected session type, or null. */
    selectedSessionTypeKey(): SessionTypeKey | null {
      return this.selectedSessionType?.sessionTypeKey ?? null
    },
    /** The pricing/feature tier (premium, traditional, basic) of the selected session type. */
    selectedSessionTypeTier(): SessionTier | null {
      const key = this.selectedSessionTypeKey
      return key ? getSessionTierByKey(key) : null
    },
    /** Whether the selected session type is in the Premium tier. */
    isPremiumTier(): boolean {
      return this.selectedSessionTypeTier === SessionTier.Premium
    },
    /** Whether the selected session type is in the Basic tier. */
    isBasicTier(): boolean {
      return this.selectedSessionTypeTier === SessionTier.Basic
    },
    /** Base price from the API DTO for the selected session type. */
    selectedBundlePrice(): number {
      return this.selectedSessionType?.primarySessionBundle.pricing?.basePrice ?? 0
    },
    /** Catalog bundle ID from the API DTO for the selected session type. */
    selectedBundleCatalogId(): string {
      return this.selectedSessionType?.primarySessionBundle.catalogBundleId ?? ''
    },
    /**
     * Numeric CADY session type ID to send to the availability and
     * scheduling endpoints. Defaults to the selected bundle's own
     * `CADY_SessionTypeId`, but escalates to a longer-duration tier when
     * add-ons attached to the cart imply a silent backend tier upgrade
     * per the SY27 merchandising spec (CSV rows 107–115). Cart line items
     * and pricing remain on the originally-selected bundle — only the
     * duration math shifts. See plan 2026.05.06-002 and ADR 010.
     */
    effectiveCadySessionTypeId(): number | null {
      const bundle = this.selectedSessionType?.primarySessionBundle
      if (!bundle) return null
      const value = getBundleIncludedGroupItemAttribute(bundle, CADY_SESSION_TYPE_ID_ATTR)
      const numeric = value ? Number(value) : NaN
      if (!Number.isFinite(numeric)) return null
      const baseId = numeric
      const addOnIds = useBookingCommerceStore().selectedAddedCadyAddOnIds
      const hasHair = addOnIds.has(CadyAddOn.Hair)
      const hasMakeup = addOnIds.has(CadyAddOn.Makeup)
      const hasCombo = hasHair && hasMakeup
      switch (baseId) {
        case CadySessionType.Elite:
          if (hasCombo) return CadySessionType.EliteUnlimited
          if (hasHair || hasMakeup) return CadySessionType.ElitePlus
          return baseId
        case CadySessionType.ElitePlus:
          if (hasCombo) return CadySessionType.EliteUnlimited
          return baseId
        case CadySessionType.MostPopular:
          if (hasHair || hasMakeup) return CadySessionType.MostPopularPlus
          return baseId
        default:
          return baseId
      }
    },
    /** Count of selected scenes. */
    selectedSceneCount(): number {
      return this.selectedSceneIds.length
    },
    /**
     * True when scene selection is complete for the current session bundle —
     * either the user has picked the required number of scenes, or the bundle
     * has no user-selectable scene group (all item groups are `isIncluded=true`).
     */
    hasValidSceneSelection(): boolean {
      const bundle = this.selectedSessionType?.primarySessionBundle
      if (!bundle) return false
      const required = getRequiredSceneCount(bundle)
      return required === 0 || this.selectedSceneIds.length >= required
    },
    /** Whether the session has been fully scheduled (location + date + time). */
    hasSessionScheduled(): boolean {
      return (
        this.scheduledLocation !== null &&
        this.scheduledSessionDate !== null &&
        this.scheduledSessionTime !== null
      )
    },
    /** Whether the presentation has been fully scheduled (date + time). */
    hasPresentationScheduled(): boolean {
      return this.scheduledPresentationDate !== null && this.scheduledPresentationTime !== null
    },
    /** Whether the selected bundle requires a presentation booking. */
    requiresPresentation(): boolean {
      const key = this.selectedSessionTypeKey
      return key ? keyRequiresPresentation(key) : false
    },
    /** Convenience getter for the scheduled location's ID. */
    scheduledLocationId(): number | null {
      return this.scheduledLocation?.LocationId ?? null
    },
    /**
     * The scheduled session time string (e.g. "2:00 PM").
     */
    scheduledTimeDisplay(): string | null {
      return this.scheduledSessionTime
    },
    /** Whether all required terms checkboxes are checked. */
    allTermsAccepted(): boolean {
      const ids = Object.keys(this.termsCheckboxes)
      if (ids.length === 0) return false
      return ids.every((id) => this.termsCheckboxes[id] === true)
    },
    /** Model release consent as a concrete boolean. An unanswered (`null`) state reads as no consent, so nothing records consent the customer never gave. */
    modelReleaseConsented(): boolean {
      return this.modelReleaseChecked === true
    },
    /** True when the customer has opted out of receiving the proof portfolio. Inverse of the opt-in checkbox. */
    portfolioOptedOut(): boolean {
      return !this.portfolioOptInChecked
    },
    /**
     * True when the customer has both a paid prepay package and Premier
     * Retouching selected. Premier supersedes the Artist Touch cart upsell
     * (Confluence "09 - Prepay" §2.2); under the prepay-before-cart order the
     * cart uses this to hide Artist Touch — which promotes B&W Proofs to the
     * primary slot — per SUN-251 item 3.1. Buy Later leaves
     * `selectedPrepayCatalogBundleId` null, so it correctly reads as "no
     * prepay" and Artist Touch stays visible.
     */
    hasPrepayAndPremierRetouching(): boolean {
      return (
        this.selectedPrepayCatalogBundleId !== null &&
        this.premierRetouchingCatalogBundleId !== null
      )
    }
  },
  actions: {
    /**
     * Sync `selectedLocationType` to match the given session type's key.
     *
     * Strict rules:
     * - RS+ keys are school-only → always force `School`.
     * - Premium keys exit the RS+ school flow → always force `Studio`.
     * - All other keys leave `selectedLocationType` unchanged — the
     *   Location Selection Modal is the authority for the school/studio
     *   decision in RS+ flows, and non-RS+ flows are initialised to
     *   `Studio` via `state()` / `resetBooking()`.
     */
    syncLocationToSessionType(sessionType: BookingSessionTypeDtoWithContent) {
      const key = sessionType.sessionTypeKey
      if (!key) return
      if (isRoadshowPlusKey(key)) {
        this.selectedLocationType = SchedulingLocationToggleType.School
      } else if (getSessionTierByKey(key) === SessionTier.Premium) {
        this.selectedLocationType = SchedulingLocationToggleType.Studio
      }
    },
    /**
     * Set the selected session type.
     * Does NOT reset scene/add-on selections — call the specific
     * clear actions if a change should reset downstream state.
     */
    setSelectedSessionType(sessionType: BookingSessionTypeDtoWithContent) {
      this.selectedSessionType = sessionType
      this.syncLocationToSessionType(sessionType)
    },
    /**
     * Upgrade to a new session type, reconciling scene selections with the
     * new tier's availability and clearing downstream add-on and scheduling
     * state. Sets `sessionBundleDirty` so the user must re-confirm Scene
     * Selection before the cart is updated.
     *
     * Per ADR 2026.04.28-001, any session-type change invalidates scheduling.
     */
    upgradeSessionType(sessionType: BookingSessionTypeDtoWithContent) {
      this.selectedSessionType = sessionType
      this.syncLocationToSessionType(sessionType)
      this.selectedSceneIds = reconcileSceneSelections(
        this.selectedSceneIds,
        sessionType.primarySessionBundle.sceneGroups,
        getMaxSceneCount(sessionType.primarySessionBundle)
      )
      this.addOnSelections = {}
      this.hairOrMakeupItemIdentifier = null
      this.clearScheduling()
      this.sessionBundleDirty = true
    },
    clearSelectedSessionType() {
      this.selectedSessionType = null
    },
    /**
     * Deselect the current session type and clear all downstream
     * scene, add-on, and scheduling state. Sets `sessionBundleDirty`
     * so the cart is updated when a new session type is selected and
     * confirmed.
     *
     * Per ADR 2026.04.28-001, any session-type change invalidates scheduling.
     */
    deselectSessionType() {
      this.selectedSessionType = null
      this.selectedSceneIds = []
      this.addOnSelections = {}
      this.hairOrMakeupItemIdentifier = null
      this.clearScheduling()
      this.sessionBundleDirty = true
    },
    /**
     * Downgrade to a different session type with specific scene selections,
     * clearing add-on and scheduling state. Sets `sessionBundleDirty` so the
     * cart is updated on the next Scene Selection confirm.
     *
     * Per ADR 2026.04.28-001, any session-type change invalidates scheduling.
     */
    downgradeSessionType(sessionType: BookingSessionTypeDtoWithContent, sceneIds: string[]) {
      this.selectedSessionType = sessionType
      this.syncLocationToSessionType(sessionType)
      this.selectedSceneIds = sceneIds
      this.addOnSelections = {}
      this.hairOrMakeupItemIdentifier = null
      this.clearScheduling()
      this.sessionBundleDirty = true
    },
    /**
     * Set the selected session type with specific scene selections.
     *
     * Per ADR 2026.04.28-001, any session-type change invalidates scheduling.
     */
    selectSessionTypeWithScenes(sessionType: BookingSessionTypeDtoWithContent, sceneIds: string[]) {
      this.selectedSessionType = sessionType
      this.syncLocationToSessionType(sessionType)
      this.selectedSceneIds = sceneIds
      this.clearScheduling()
    },
    // --- Location selection ---
    setSelectedLocation(location: LandingLocationOption) {
      this.selectedLocation = location
    },
    clearSelectedLocation() {
      this.selectedLocation = null
    },
    // --- Scene selections ---
    /**
     * Add a scene if not selected, or remove it if already selected.
     *
     * Per ADR 2026.04.28-001, any scene change invalidates scheduling.
     */
    toggleSceneSelection(sceneId: string) {
      const index = this.selectedSceneIds.indexOf(sceneId)
      if (index === -1) {
        this.selectedSceneIds.push(sceneId)
      } else {
        this.selectedSceneIds.splice(index, 1)
      }
      this.clearScheduling()
    },
    /**
     * Replace the full scene selection set.
     *
     * Per ADR 2026.04.28-001, any scene change invalidates scheduling.
     */
    setSelectedSceneIds(sceneIds: string[]) {
      this.selectedSceneIds = sceneIds
      this.clearScheduling()
    },
    clearSelectedSceneIds() {
      this.selectedSceneIds = []
    },
    // --- Cart sync ---
    /** Clear the dirty flag after a successful cart upsert at Scene Selection. */
    clearSessionBundleDirty() {
      this.sessionBundleDirty = false
    },
    // --- Scene favorites ---
    /** Add a scene to favorites if not hearted, or remove it if already hearted. */
    toggleSceneFavorite(sceneId: string) {
      const index = this.favoritedSceneIds.indexOf(sceneId)
      if (index === -1) {
        this.favoritedSceneIds.push(sceneId)
      } else {
        this.favoritedSceneIds.splice(index, 1)
      }
    },
    setFavoritedSceneIds(sceneIds: string[]) {
      this.favoritedSceneIds = sceneIds
    },
    clearFavoritedSceneIds() {
      this.favoritedSceneIds = []
    },
    // --- Add-on selections ---
    setAddOnSelection(sectionId: string, state: AddOnSelectionState) {
      this.addOnSelections = { ...this.addOnSelections, [sectionId]: state }
    },
    clearAddOnSelections() {
      this.addOnSelections = {}
      this.hairOrMakeupItemIdentifier = null
    },
    setHairOrMakeupItemIdentifier(identifier: string | null) {
      this.hairOrMakeupItemIdentifier = identifier
    },
    // --- Scheduling ---
    setLocationType(type: SchedulingLocationToggleType) {
      this.selectedLocationType = type
    },
    setScheduledSession(
      location: LocationModel,
      date: string,
      time: string,
      surchargeTier: string | null,
      duration: number,
      sig: string
    ) {
      this.scheduledLocation = location
      this.scheduledSessionDate = date
      this.scheduledSessionTime = time
      this.scheduledSessionSurchargeTier = surchargeTier
      this.scheduledSessionDuration = duration
      this.scheduledSessionSig = sig
      // A freshly committed slot invalidates any prior hold on the old slot.
      this.sessionAppointmentReservationId = null
      this.sessionAppointmentExpiration = null
    },
    setPresentationSessionTypeId(id: number) {
      this.presentationSessionTypeId = id
    },
    clearScheduledSession() {
      this.scheduledLocation = null
      this.scheduledSessionDate = null
      this.scheduledSessionTime = null
      this.scheduledSessionSurchargeTier = null
      this.scheduledSessionDuration = null
      this.scheduledSessionSig = null
      this.sessionAppointmentReservationId = null
      this.sessionAppointmentExpiration = null
      this.presentationSessionTypeId = null
    },
    setScheduledPresentation(
      date: string,
      time: string,
      surchargeTier: string | null,
      duration: number,
      sig: string
    ) {
      this.scheduledPresentationDate = date
      this.scheduledPresentationTime = time
      this.scheduledPresentationSurchargeTier = surchargeTier
      this.scheduledPresentationDuration = duration
      this.scheduledPresentationSig = sig
      // A freshly committed slot invalidates any prior hold on the old slot.
      this.presentationAppointmentReservationId = null
      this.presentationAppointmentExpiration = null
    },
    clearScheduledPresentation() {
      this.scheduledPresentationDate = null
      this.scheduledPresentationTime = null
      this.scheduledPresentationSurchargeTier = null
      this.scheduledPresentationDuration = null
      this.scheduledPresentationSig = null
      this.presentationAppointmentReservationId = null
      this.presentationAppointmentExpiration = null
    },
    /**
     * Places a temporary hold on the committed session appointment slot via
     * `ReserveAppointment`, storing the resulting reservation ID + expiration.
     */
    async reserveSessionAppointment(): Promise<AppointmentReservationOutcome> {
      const sessionTypeId = this.effectiveCadySessionTypeId
      const locationId = this.scheduledLocationId
      const mainStore = useMainStore()
      const schoolId = mainStore.selectedSchool?.schoolId
      if (
        !sessionTypeId ||
        !locationId ||
        !schoolId ||
        !this.scheduledSessionDate ||
        !this.scheduledSessionTime ||
        this.scheduledSessionDuration === null
      ) {
        console.error('[bookingSelectionsStore] reserveSessionAppointment: missing required fields')
        return { success: false }
      }
      try {
        const cartStore = useCartStore()
        const previousSessionDeviceId = this.sessionAppointmentDeviceId
        const request: ReserveAppointmentRequest = {
          DeviceId: previousSessionDeviceId,
          CartId: cartStore.cartId ?? null,
          SessionTypeId: sessionTypeId,
          SchoolId: schoolId,
          LocationId: locationId,
          AppointmentStart: combineDateAndTime(
            this.scheduledSessionDate,
            this.scheduledSessionTime
          ) as unknown as Date,
          SceneIds: cartStore.cadySceneEnumValuesInCart,
          DurationMinutes: RESERVATION_HOLD_DURATION_MINUTES
        }
        const service = new Sunny16CommerceCadyService()
        const result = await service.reserveAppointment(request)
        // A 200 response with `AppointmentReservationId: 0` is a soft failure
        // (e.g. the slot was taken by someone else) — the API surfaces this
        // as a successful HTTP call carrying a customer-facing `Message`,
        // not as a thrown error.
        if (!result.AppointmentReservationId) {
          console.error(
            '[bookingSelectionsStore] reserveSessionAppointment: slot unavailable',
            result.Message
          )
          return { success: false, message: result.Message ?? undefined }
        }
        this.sessionAppointmentReservationId = result.AppointmentReservationId
        // Typed `Date` on the DTO, but arrives over the wire as an ISO string (axios doesn't auto-convert).
        this.sessionAppointmentExpiration = result.Expiration as unknown as string
        if (result.DeviceId) {
          this.sessionAppointmentDeviceId = result.DeviceId
        }
        return { success: true }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to reserve appointment'
        console.error('[bookingSelectionsStore] reserveSessionAppointment failed', error)
        return { success: false, message }
      }
    },
    /**
     * Places a temporary hold on the committed presentation appointment slot
     * via `ReserveAppointment`, storing the resulting reservation ID + expiration.
     */
    async reservePresentationAppointment(): Promise<AppointmentReservationOutcome> {
      const sessionTypeId = this.presentationSessionTypeId
      const locationId = this.scheduledLocationId
      const mainStore = useMainStore()
      const schoolId = mainStore.selectedSchool?.schoolId
      if (
        !sessionTypeId ||
        !locationId ||
        !schoolId ||
        !this.scheduledPresentationDate ||
        !this.scheduledPresentationTime ||
        this.scheduledPresentationDuration === null
      ) {
        console.error(
          '[bookingSelectionsStore] reservePresentationAppointment: missing required fields'
        )
        return { success: false }
      }
      try {
        const cartStore = useCartStore()
        const previousPresentationDeviceId = this.presentationAppointmentDeviceId
        const request: ReserveAppointmentRequest = {
          DeviceId: previousPresentationDeviceId,
          CartId: cartStore.cartId ?? null,
          SessionTypeId: sessionTypeId,
          SchoolId: schoolId,
          LocationId: locationId,
          AppointmentStart: combineDateAndTime(
            this.scheduledPresentationDate,
            this.scheduledPresentationTime
          ) as unknown as Date,
          SceneIds: [],
          DurationMinutes: RESERVATION_HOLD_DURATION_MINUTES
        }
        const service = new Sunny16CommerceCadyService()
        const result = await service.reserveAppointment(request)
        // A 200 response with `AppointmentReservationId: 0` is a soft failure
        // (e.g. the slot was taken by someone else) — the API surfaces this
        // as a successful HTTP call carrying a customer-facing `Message`,
        // not as a thrown error.
        if (!result.AppointmentReservationId) {
          console.error(
            '[bookingSelectionsStore] reservePresentationAppointment: slot unavailable',
            result.Message
          )
          return { success: false, message: result.Message ?? undefined }
        }
        this.presentationAppointmentReservationId = result.AppointmentReservationId
        // Typed `Date` on the DTO, but arrives over the wire as an ISO string (axios doesn't auto-convert).
        this.presentationAppointmentExpiration = result.Expiration as unknown as string
        if (result.DeviceId) {
          this.presentationAppointmentDeviceId = result.DeviceId
        }
        return { success: true }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to reserve appointment'
        console.error('[bookingSelectionsStore] reservePresentationAppointment failed', error)
        return { success: false, message }
      }
    },
    /**
     * Clear all scheduling state (session + presentation). Also clears
     * accepted terms — terms acceptance is bound to a specific scheduled
     * session, so a wiped schedule must wipe acceptance with it. Otherwise
     * a back-button + scene-change race could land the user on
     * `/checkout/pay` with terms still satisfied but no schedule attached.
     */
    clearScheduling() {
      this.clearScheduledSession()
      this.clearScheduledPresentation()
      this.resetTermsAcceptance()
    },
    /**
     * Persist scheduling selections to the cart as prompt customizations.
     * Builds CartItemCustomizationDto objects from the stored date/time values
     * and sends them to the cart API via upsertCartItemCustomizations.
     *
     * TODO SCHEDULING API: This action is stubbed — prompt IDs and the cart item
     * lookup strategy in schedulingMapper.ts need to be finalized once the API
     * schema for scheduling prompts is available.
     */
    async saveSchedulingToCart(): Promise<{ success: boolean }> {
      if (
        !this.scheduledSessionDate ||
        !this.scheduledSessionTime ||
        !this.scheduledSessionSig ||
        this.scheduledSessionDuration === null
      ) {
        return { success: false }
      }
      const sessionTypeId = this.effectiveCadySessionTypeId
      if (!sessionTypeId || !this.scheduledLocationId) {
        console.error(
          '[bookingSelectionsStore] saveSchedulingToCart: missing location or session type ID'
        )
        return { success: false }
      }
      const cartStore = useCartStore()
      const cartBundle = cartStore.getCartBundleByCatalogBundleId(this.selectedBundleCatalogId)
      if (!cartBundle) {
        console.error('[bookingSelectionsStore] saveSchedulingToCart: no cart bundle found')
        return { success: false }
      }
      const customizations = buildSchedulingCustomizations({
        sessionCartItems: cartBundle.cartItems,
        locationId: this.scheduledLocationId,
        locationName: this.scheduledLocation!.LocationName,
        locationAddress: formatLocationFullAddress(this.scheduledLocation!),
        sessionTypeId,
        sessionDate: this.scheduledSessionDate,
        sessionTime: this.scheduledSessionTime,
        sessionSurchargeTier: this.scheduledSessionSurchargeTier,
        sessionDuration: this.scheduledSessionDuration,
        sessionSig: this.scheduledSessionSig,
        presentationDate: this.scheduledPresentationDate,
        presentationTime: this.scheduledPresentationTime,
        presentationSurchargeTier: this.scheduledPresentationSurchargeTier,
        presentationDuration: this.scheduledPresentationDuration,
        presentationSig: this.scheduledPresentationSig,
        presentationSessionTypeId: this.presentationSessionTypeId
      })
      if (customizations.length === 0) {
        console.error('[bookingSelectionsStore] saveSchedulingToCart: no customizations built')
        return { success: false }
      }
      try {
        await cartStore.upsertCartItemCustomizations(customizations)
        // TODO: Add Google Analytics tracking
        return { success: true }
      } catch (error) {
        console.error('[bookingSelectionsStore] saveSchedulingToCart failed', error)
        return { success: false }
      }
    },
    // --- Scene state ---
    /**
     * Clear both scene selections and scene favorites.
     */
    clearSceneState() {
      this.selectedSceneIds = []
      this.favoritedSceneIds = []
    },
    // --- Cart upsell decline tracking ---
    declineCartUpsell(catalogBundleId: string) {
      if (!this.declinedCartUpsellIds.includes(catalogBundleId)) {
        this.declinedCartUpsellIds.push(catalogBundleId)
      }
    },
    undeclineCartUpsell(catalogBundleId: string) {
      this.declinedCartUpsellIds = this.declinedCartUpsellIds.filter((id) => id !== catalogBundleId)
    },
    clearDeclinedCartUpsells() {
      this.declinedCartUpsellIds = []
    },
    // --- Prepay selection ---
    setSelectedPrepayOption(optionId: string, catalogBundleId: string | null = null) {
      this.selectedPrepayOption = optionId
      this.selectedPrepayCatalogBundleId = catalogBundleId
    },
    selectBuyLater() {
      this.selectedPrepayOption = BUY_LATER_OPTION
      this.selectedPrepayCatalogBundleId = null
    },
    clearPrepaySelection() {
      this.selectedPrepayOption = null
      this.selectedPrepayCatalogBundleId = null
    },
    // --- Premier Retouching ---
    openRetouchingDrawer() {
      this.showRetouchingDrawer = true
    },
    closeRetouchingDrawer() {
      this.showRetouchingDrawer = false
    },
    /** Record the user's retouching choice and auto-close the drawer. */
    setPremierRetouching(added: boolean, catalogBundleId: string | null = null) {
      this.premierRetouchingCatalogBundleId = added ? catalogBundleId : null
      this.showRetouchingDrawer = false
    },
    /** Reset retouching to undecided and close the drawer. */
    clearPremierRetouching() {
      this.premierRetouchingCatalogBundleId = null
      this.showRetouchingDrawer = false
    },
    // --- Terms acceptance ---
    /** Toggle a terms checkbox. Defaults to false if the key doesn't exist yet. */
    toggleTermsCheckbox(checkboxId: string) {
      const current = this.termsCheckboxes[checkboxId] ?? false
      this.termsCheckboxes = { ...this.termsCheckboxes, [checkboxId]: !current }
    },
    /** Create a fresh checkboxes map with all IDs set to unchecked. */
    initializeTermsCheckboxes(checkboxIds: string[]) {
      const state: Record<string, boolean> = {}
      for (const id of checkboxIds) {
        state[id] = false
      }
      this.termsCheckboxes = state
    },
    setModelReleaseChecked(checked: boolean) {
      this.modelReleaseChecked = checked
    },
    setPortfolioOptInChecked(checked: boolean) {
      this.portfolioOptInChecked = checked
    },
    /**
     * Clear per-checkbox terms acceptance. Deliberately leaves `modelReleaseChecked` alone: it records the customer's
     * answer to a consent question rather than acceptance bound to a scheduled session, and clearing it here would let
     * the proof-type default re-check the box on the next visit to the Terms page — silently turning an explicit
     * refusal back into consent. Only `resetBooking()` clears it, because a brand-new booking has no prior answer.
     */
    resetTermsAcceptance() {
      this.termsCheckboxes = {}
      this.portfolioOptInChecked = true
    },
    // --- Composite actions ---
    /**
     * Clear declined cart-upsell tracking.
     */
    resetCheckoutSelections() {
      this.declinedCartUpsellIds = []
    },
    // --- Bulk reset ---
    /** Reset all booking-flow selections. Called when starting a new booking. */
    resetBooking() {
      this.selectedLocation = null
      this.selectedSessionType = null
      this.selectedSceneIds = []
      this.favoritedSceneIds = []
      this.addOnSelections = {}
      this.hairOrMakeupItemIdentifier = null
      this.selectedLocationType = SchedulingLocationToggleType.Studio
      this.clearScheduling()
      this.sessionBundleDirty = false
      this.resetCheckoutSelections()
      this.clearPrepaySelection()
      this.clearPremierRetouching()
      this.resetTermsAcceptance()
      this.modelReleaseChecked = null
    }
  },
  persist: true
})
